import { instantISO } from '@mononoke/shared';
import { api } from './api.js';
import type { SendFn } from './op-queue.js';
import type { PendingOp } from './pending-ops.js';
import { fetchPut, type PutFn } from './uploads.js';

/**
 * What each op KIND means on the wire — offline increments 2 and 3.
 *
 * `op-queue.ts` deliberately knows none of this (ordering, retry, expiry and visibility
 * only), which is what lets the whole replay loop be proven under vitest with no network.
 * This module is the other half: one `switch` and nothing else, so adding a kind is
 * adding a case rather than touching the loop.
 *
 * EVERY OP SENDS ITS OWN `id` AS THE IDEMPOTENCY KEY. That is the single rule that makes
 * replay safe (migration 016), and it is why the id is minted once at enqueue and never
 * regenerated on retry.
 */

/**
 * A FRESH UPLOAD URL PER ATTEMPT, and this is the constraint that shaped the queue.
 *
 * `signPut(key, 900)` — a presigned PUT URL lives fifteen minutes. A photo captured in a
 * basement stockroom and replayed when signal returns hours later CANNOT use the URL it
 * was captured with, so persisting the URL would produce a queue whose every replay
 * failed with a signature error the classifier would (correctly) call permanent.
 *
 * So the blob is what persists, and the URL — and the key with it — is minted at send
 * time. Re-signing the ORIGINAL key was the alternative and was rejected: upload keys
 * carry no tenant in their path (`items/original/<uuid>.jpg`), so a route handing out
 * write URLs for keys the caller did not mint is a cross-tenant overwrite waiting for
 * someone to learn a uuid. Minting a new key costs one wasted object in the rare
 * replay-after-success case, which is already this codebase's accepted condition.
 */
async function sendPhotoUpload(op: Extract<PendingOp, { kind: 'photo-upload' }>, put: PutFn): Promise<void> {
  const minted = await api.uploadUrls.mutate({ count: 1 });
  const upload = minted.uploads[0];
  if (!upload) throw new Error('the server minted no upload URL');

  await put(upload.url, op.blob);

  await api.attachPhoto.mutate({
    itemId: op.payload.itemId,
    objectKey: upload.key,
    position: op.payload.position,
    idempotencyKey: op.id,
  });
}

/**
 * A check-out or check-in captured offline — increment 3, and the plan's own example of
 * why receipts had to land first.
 *
 * `occurredAt` IS THE OP'S CREATION INSTANT, not `now()`. That is the whole point of
 * §2.3: a garment that left the building at 17:00 must not be recorded as leaving at
 * 19:00 because that is when the tunnel recovered. The server decides whether to believe
 * the claim (`isPlausibleClientInstant`) and records its own instant alongside either
 * way, so a wrong device clock costs precision on one row and nothing else.
 */
async function sendMovement(op: Extract<PendingOp, { kind: 'movement' }>): Promise<void> {
  const input = {
    itemId: op.payload.itemId,
    ...(op.payload.note !== undefined ? { note: op.payload.note } : {}),
    // Carried so an offline booking check-in lands in the booking's history on replay,
    // the same as the online attempt would (frame 4d). Absent for booking-less movements.
    ...(op.payload.bookingId !== undefined ? { bookingId: op.payload.bookingId } : {}),
    // `instantISO`, not `toISOString()`: the guardrail lint rule refuses the latter, and
    // it is right to — this IS an instant rather than a calendar date, and the shared
    // helper is the one place that distinction is made deliberately.
    occurredAt: instantISO(new Date(op.createdAt)),
    idempotencyKey: op.id,
  };
  if (op.payload.direction === 'out') await api.checkOutItem.mutate(input);
  else await api.checkInItem.mutate(input);
}

/**
 * Upload one blob and attach it to an item, under a CALLER-SUPPLIED idempotency key.
 *
 * The key is a parameter rather than `op.id` because an intake sends several photos under
 * one op, and each needs its own stable identity — see `sendIntake`.
 */
async function uploadAndAttach(
  itemId: string,
  position: number,
  blob: Blob,
  idempotencyKey: string,
  put: PutFn,
): Promise<void> {
  const minted = await api.uploadUrls.mutate({ count: 1 });
  const upload = minted.uploads[0];
  if (!upload) throw new Error('the server minted no upload URL');
  await put(upload.url, blob);
  await api.attachPhoto.mutate({ itemId, objectKey: upload.key, position, idempotencyKey });
}

/**
 * A condition flag captured offline — the student unpacking a rack in a basement is
 * exactly the person with no signal, and a flag raised twice is a second damage report
 * somebody has to read and dismiss.
 *
 * THE PHOTO GOES UP FIRST, then the flag references it. That ordering is the codebase's
 * standing discipline (row after object) and it matters more here than usual: a flag row
 * pointing at a key that was never uploaded is damage evidence with a broken link, which
 * is worse than a flag with no photo at all.
 */
async function sendFlag(op: Extract<PendingOp, { kind: 'flag' }>, put: PutFn): Promise<void> {
  let photoKey: string | undefined;
  if (op.blob) {
    const minted = await api.uploadUrls.mutate({ count: 1 });
    const upload = minted.uploads[0];
    if (!upload) throw new Error('the server minted no upload URL');
    await put(upload.url, op.blob);
    photoKey = upload.key;
  }
  await api.raiseFlag.mutate({
    itemId: op.payload.itemId,
    reason: op.payload.reason,
    presetId: op.payload.presetId,
    photoKey,
    idempotencyKey: op.id,
  });
}

/**
 * A whole intake — the item and its photos, replayed as one act.
 *
 * DERIVED KEYS ARE WHAT MAKE THIS SAFE. `createItem` uses the op's own id; photo *n* uses
 * `${op.id}-p${n}`. Every one of them is deterministic, so a replay that got halfway
 * through meets its own receipts for the steps already done and only performs the rest.
 * The alternative — enqueuing separate photo ops once the item exists — was rejected: it
 * needs two durable writes (enqueue the children, then delete the parent) with no
 * transaction around them, so a crash between the two either loses the photos or repeats
 * the intake.
 *
 * SEQUENTIAL, not parallel: the photos' `position` is their order in the strip, and
 * firing them at once would leave that to the network.
 */
async function sendIntake(op: Extract<PendingOp, { kind: 'intake' }>, put: PutFn): Promise<void> {
  const created = await api.createItem.mutate({ ...op.payload, idempotencyKey: op.id });
  for (const [position, blob] of op.blobs.entries()) {
    await uploadAndAttach(created.id, position, blob, `${op.id}-p${String(position)}`, put);
  }
}

export function createOpSend(put: PutFn = fetchPut): SendFn {
  return async (op: PendingOp): Promise<void> => {
    switch (op.kind) {
      case 'photo-upload':
        return sendPhotoUpload(op, put);
      case 'movement':
        return sendMovement(op);
      case 'flag':
        return sendFlag(op, put);
      case 'intake':
        return sendIntake(op, put);
    }
  };
}
